import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramBoilerOperatingCost } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';

const KEY = 'tabai.calc.hydronicBoilerOperatingCost.v1';

type Fields = { fireRate: string; fuelCost: string; hours: string; cost: string };
const EMPTY: Fields = { fireRate: '', fuelCost: '', hours: '', cost: '' };

/**
 * Boiler Operating Cost — Cost = Fire Rate × Fuel Cost × Hours
 * Bidirectional: fill any 3 of 4 fields.
 */
const HydronicBoilerOperatingCostScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of the 4 fields to solve for the missing one.'); return; }
    if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

    const fr = parseFloat(fields.fireRate);
    const fc = parseFloat(fields.fuelCost);
    const h = parseFloat(fields.hours);
    const c = parseFloat(fields.cost);

    if (fields.cost === '') {
      if (!(fr > 0)) { setError('Fire rate must be greater than zero.'); return; }
      if (!Number.isFinite(fc) || fc < 0) { setError('Fuel cost cannot be negative.'); return; }
      if (!(h > 0)) { setError('Operating hours must be greater than zero.'); return; }
      setSolvedFor('cost');
      set({ cost: parseFloat((fr * fc * h).toFixed(2)).toString() });
    } else if (fields.fireRate === '') {
      if (!(fc > 0)) { setError('Fuel cost must be greater than zero.'); return; }
      if (!(h > 0)) { setError('Operating hours must be greater than zero.'); return; }
      if (!Number.isFinite(c) || c < 0) { setError('Cost cannot be negative.'); return; }
      setSolvedFor('fireRate');
      set({ fireRate: parseFloat((c / (fc * h)).toFixed(4)).toString() });
    } else if (fields.fuelCost === '') {
      if (!(fr > 0)) { setError('Fire rate must be greater than zero.'); return; }
      if (!(h > 0)) { setError('Operating hours must be greater than zero.'); return; }
      if (!Number.isFinite(c) || c < 0) { setError('Cost cannot be negative.'); return; }
      setSolvedFor('fuelCost');
      set({ fuelCost: parseFloat((c / (fr * h)).toFixed(4)).toString() });
    } else if (fields.hours === '') {
      if (!(fr > 0)) { setError('Fire rate must be greater than zero.'); return; }
      if (!(fc > 0)) { setError('Fuel cost must be greater than zero.'); return; }
      if (!Number.isFinite(c) || c < 0) { setError('Cost cannot be negative.'); return; }
      setSolvedFor('hours');
      set({ hours: parseFloat((c / (fr * fc)).toFixed(2)).toString() });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.fireRate && { label: 'Fire Rate', value: fields.fireRate, unit: 'lbs/h or gal/h', emphasis: solvedFor === 'fireRate' },
    fields.fuelCost && { label: 'Fuel Cost', value: fields.fuelCost, unit: '$/lb or $/gal', emphasis: solvedFor === 'fuelCost' },
    fields.hours && { label: 'Operating Hours', value: fields.hours, unit: 'h', emphasis: solvedFor === 'hours' },
    fields.cost && { label: 'Operating Cost', value: fields.cost, unit: '$', emphasis: solvedFor === 'cost' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Boiler Operating Cost"
      subtitle="Solve for cost, fire rate, fuel cost, or hours using Cost = Fire Rate × Fuel Cost × Hours"
      formula={
        <>
          <FormulaLine>Cost = Fire Rate × Fuel Cost × Hours</FormulaLine>
          <FormulaNote>Fire Rate (lbs/h or gal/h), Fuel Cost ($/lb or $/gal), Hours = operating hours</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Cost = Fire Rate × Fuel Cost × Hours"
      diagram={<DiagramBoilerOperatingCost />}
      outputSlots={{
        boiler_fire_rate_gph: fields.fireRate ? parseFloat(fields.fireRate) : undefined,
        boiler_fuel_cost: fields.fuelCost ? parseFloat(fields.fuelCost) : undefined,
        boiler_operating_hours: fields.hours ? parseFloat(fields.hours) : undefined,
        boiler_operating_cost: fields.cost ? parseFloat(fields.cost) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.boiler_fire_rate_gph != null && !fields.fireRate) {
          patch.fireRate = parseFloat(Number(values.boiler_fire_rate_gph).toFixed(4)).toString();
        }
        if (values.boiler_fuel_cost != null && !fields.fuelCost) {
          patch.fuelCost = parseFloat(Number(values.boiler_fuel_cost).toFixed(4)).toString();
        }
        if (values.boiler_operating_hours != null && !fields.hours) {
          patch.hours = parseFloat(Number(values.boiler_operating_hours).toFixed(2)).toString();
        }
        if (values.boiler_operating_cost != null && !fields.cost) {
          patch.cost = parseFloat(Number(values.boiler_operating_cost).toFixed(2)).toString();
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Fire Rate" value={fields.fireRate} onChangeText={(v) => set({ fireRate: v })} rightText="lbs/h" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Fuel Cost" value={fields.fuelCost} onChangeText={(v) => set({ fuelCost: v })} rightText="$/lb" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Operating Hours" value={fields.hours} onChangeText={(v) => set({ hours: v })} rightText="h" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Operating Cost" value={fields.cost} onChangeText={(v) => set({ cost: v })} rightText="$" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default HydronicBoilerOperatingCostScreen;
